import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiSearch, FiAlertCircle } from 'react-icons/fi';
import { AdminLayout } from '../../components/layout';
import { Card, Button, Loading } from '../../components/common';
import { getUserByEmail, assignManager } from '../../firebase/users';
import { getAllCompanies } from '../../firebase/companies';

const ManagerAdd = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(true);
  
  const [email, setEmail] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [error, setError] = useState('');
  const [searchError, setSearchError] = useState('');

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const result = await getAllCompanies();
      setCompanies(result.companies?.filter(c => c.status === 'active') || []);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleSearchUser = async () => {
    if (!email.trim()) {
      setSearchError('Please enter an email address');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSearchError('Please enter a valid email address');
      return;
    }

    setSearchLoading(true);
    setSearchError('');
    setFoundUser(null);

    try {
      const result = await getUserByEmail(email.trim());
      
      if (result.user) {
        if (result.user.role === 'admin') {
          setSearchError('This user is already an admin and cannot be assigned as manager');
        } else if (result.user.role === 'manager') {
          setSearchError('This user is already a manager');
        } else {
          setFoundUser(result.user);
        }
      } else {
        setSearchError('No user found with this email. The user must sign up first before being assigned as a manager.');
      }
    } catch (error) {
      console.error('Error searching user:', error);
      setSearchError('Error searching for user. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!foundUser) {
      setError('Please search and select a user first');
      return;
    }

    if (!selectedCompanyId) {
      setError('Please select a company');
      return;
    }

    setLoading(true);

    try {
      const result = await assignManager(foundUser.id, selectedCompanyId);

      if (result.error) {
        setError(result.error);
        setLoading(false);
        return;
      }

      navigate('/admin/managers', {
        state: { message: `Manager assigned successfully!` }
      });

    } catch (error) {
      console.error('Error assigning manager:', error);
      setError('Failed to assign manager. Please try again.');
      setLoading(false);
    }
  };

  if (companiesLoading) {
    return (
      <AdminLayout title="Add Manager">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Add Manager">
      <form onSubmit={handleSubmit}>
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Error Alert */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <FiAlertCircle className="text-red-500 flex-shrink-0" size={20} />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Instructions */}
          <Card className="p-6 bg-blue-50 border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2">How to assign a manager</h3>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li>The user must first create an account on the platform</li>
              <li>Search for the user by their email address</li>
              <li>Select the company they should manage</li>
              <li>Click "Assign Manager" to grant access</li>
            </ol>
          </Card>

          {/* Search User */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Find User</h3>
            
            <div className="flex gap-3">
              <div className="flex-1">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    setSearchError('');
                    setFoundUser(null);
                  }}
                  placeholder="Enter user's email address"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <Button
                type="button"
                onClick={handleSearchUser}
                disabled={searchLoading}
                className="flex items-center gap-2"
              >
                {searchLoading ? <Loading size="sm" /> : <FiSearch size={18} />}
                Search
              </Button>
            </div>
            
            {searchError && (
              <p className="text-red-500 text-sm mt-2">{searchError}</p>
            )}

            {/* Found User */}
            {foundUser && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    {foundUser.photoURL ? (
                      <img 
                        src={foundUser.photoURL} 
                        alt={foundUser.displayName}
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-xl font-semibold text-green-600">
                        {(foundUser.displayName || foundUser.email)?.[0]?.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {foundUser.displayName || 'No Name'}
                    </p>
                    <p className="text-sm text-gray-600">{foundUser.email}</p>
                  </div>
                  <span className="ml-auto px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                    User Found
                  </span>
                </div>
              </div>
            )}
          </Card>

          {/* Select Company */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Select Company</h3>
            
            {companies.length > 0 ? (
              <div className="space-y-2">
                {companies.map((company) => (
                  <label
                    key={company.id}
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedCompanyId === company.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="company"
                      value={company.id}
                      checked={selectedCompanyId === company.id}
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                      className="w-4 h-4 text-primary-600"
                    />
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                      {company.logo ? (
                        <img src={company.logo} alt={company.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-lg font-bold text-gray-400">
                          {company.name[0]}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{company.name}</p>
                      <p className="text-sm text-gray-500">{company.slug}.carbooking.com</p>
                    </div>
                  </label>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No active companies available.</p>
                <p className="text-sm">Create a company first before assigning managers.</p>
              </div>
            )}
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/managers')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !foundUser || !selectedCompanyId}
              className="flex items-center gap-2"
            >
              {loading ? <Loading size="sm" /> : <FiSave size={18} />}
              Assign Manager
            </Button>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
};

export default ManagerAdd;
